import { useCallback, useEffect, useState } from 'react';
import { Camera } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { CycleDayStepper } from '../../components/onboarding/CycleDayStepper';
import { WizardErrorCallout, WizardHelpNote, WizardStepHeader } from '../../components/wizard';
import { useOnboardingFooter } from '../../hooks/useOnboardingFooter';
import { authAPI, guidedFlowAPI } from '../../data/auth';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';
import { clampSelfieDay } from '../../utils/guidedFlowUtils';

export function OnboardingCycleDay() {
  const navigate = useNavigate();
  const { profile, refreshMe } = useAuth();
  const [day, setDay] = useState(() => clampSelfieDay(profile?.selfie_day_of_month ?? 1));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (profile?.selfie_day_of_month != null) {
      setDay(clampSelfieDay(profile.selfie_day_of_month));
    }
  }, [profile?.selfie_day_of_month]);

  const onContinue = useCallback(async () => {
    const normalized = clampSelfieDay(day);
    setLoading(true);
    setError(null);

    const profileRes = await authAPI.patchProfile({ selfie_day_of_month: normalized });
    if (!profileRes.success) {
      setError(pt.auth.errors.generic);
      setLoading(false);
      return;
    }

    await guidedFlowAPI.markWizardSeen('selfie_day_setup');
    await authAPI.saveOnboardingStep('method');
    await refreshMe();
    setLoading(false);
    navigate('/onboarding/method');
  }, [day, navigate, refreshMe]);

  useOnboardingFooter({
    primaryLabel: pt.onboarding.common.continue,
    onPrimary: onContinue,
    loading,
  });

  return (
    <div className="flex flex-col gap-space-6">
      <div className="onboarding-hero h-[140px] rounded-ds-xl">
        <Camera className="h-10 w-10 text-sage-500 opacity-70 dark:text-sage-400" aria-hidden />
      </div>
      <WizardStepHeader
        eyebrow={pt.onboarding.cycleDay.eyebrow}
        title={pt.onboarding.cycleDay.title}
        subtitle={pt.onboarding.cycleDay.subtitle}
      />
      <CycleDayStepper value={day} onChange={setDay} className="mt-0" />
      <WizardHelpNote tone="sage">{pt.onboarding.cycleDay.hint}</WizardHelpNote>
      {error ? <WizardErrorCallout>{error}</WizardErrorCallout> : null}
    </div>
  );
}
